/**
 * Journey Registry Configuration
 *
 * Single source of truth for checkout journeys. Combines individual journey
 * configurations and provides helper functions for accessing journey data.
 *
 * Add new journeys by creating a new journey-*.ts file and importing it here.
 */

import { Router } from '@angular/router';

import { CheckoutJourney, JourneyPageConfig, CheckoutStep } from '../models/checkout-journey.types';
import { BookingTypeName } from '@shared/models/enums';
import { Booking } from '@shared/models/booking';
import { UNION_JOURNEY } from './journey-union-booking';
import { EVENT_REQUEST_JOURNEY } from './journey-event-request';
import { MATCH_JOURNEY } from './journey-match';
import { JourneyStore } from '../journey.store';

/**
 * Journey Registry - Single source of truth for checkout journeys
 * Combines individual journey configurations using BookingTypeName as key
 * Note: Only includes journey configurations for booking types (UNION, MATCH, EVENT)
 */
export const CHECKOUT_JOURNEYS: Partial<Record<BookingTypeName, CheckoutJourney>> = {
  [BookingTypeName.UNION]: UNION_JOURNEY,
  [BookingTypeName.MATCH]: MATCH_JOURNEY,
  [BookingTypeName.EVENT]: EVENT_REQUEST_JOURNEY,
};

/**
 * Get journey configuration by type (uses BookingTypeName)
 */
export function getJourneyConfig(bookingType: BookingTypeName): CheckoutJourney {
  const config = CHECKOUT_JOURNEYS[bookingType];
  if (!config) {
    console.warn(
      `[Checkout Journeys] Journey configuration not found in registry for booking type: ${bookingType}. Available types: ${Object.keys(CHECKOUT_JOURNEYS).join(', ')}`,
    );
  }
  return config;
}

/**
 * Get page configuration for journey by route
 */
export function getPageConfig(bookingType: BookingTypeName, route: string): JourneyPageConfig | undefined {
  const config = getJourneyConfig(bookingType);
  if (!config) {
    console.warn(
      `[Checkout Journeys] Cannot find page config - missing journey configuration for booking type: ${bookingType}, route: ${route}`,
    );
    return undefined;
  }
  return config.page.find((p) => p.pageRoute === route);
}

/**
 * Extract step indicator config from journey
 */
export function getStepIndicatorConfig(bookingType: BookingTypeName): CheckoutStep[] {
  const config = getJourneyConfig(bookingType);
  if (!config) {
    console.warn(
      `[Checkout Journeys] Cannot extract step indicator config - missing journey configuration for booking type: ${bookingType}`,
    );
    return [];
  }
  return config.page.map((page, index) => ({
    stepNumber: index + 1,
    route: `/checkout/${page.pageRoute}`,
    ariaLabel: page.ariaLabel,
  }));
}

/**
 * Navigate to user's last search
 */
export async function navigateToLastUserSearch(
  journeyStore: InstanceType<typeof JourneyStore>,
  router: Router,
): Promise<void> {
  const JouneySearchRoute = journeyStore.journeyConfig()?.searchRoute;
  const params = journeyStore.lastSearchParams();

  // Validate params before navigation
  if (!params.locationId || !params.activityId || !params.start || !params.end) {
    console.warn('[navigateToLastUserSearch] Cannot navigate - invalid search params:', params);
    return;
  }

  const queryParams = {
    activityId: params.activityId,
    locationId: params.locationId,
    start: params.start,
    end: params.end,
  };

  await router.navigate([JouneySearchRoute], {
    queryParams,
    queryParamsHandling: 'merge',
  });
}

/**
 * Validation helper: Check if all booking periods have valid addon selections
 * This can be enhanced with actual validation logic
 */
export function isAllAddonsValid(booking: Booking | null): boolean {
  if (!booking?.bookingPeriods) return false;
  // Add your validation logic here
  // For now, return true to allow progression
  return true;
}
